import { useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

/**
 * 2026-10-22 · 通用的手机端筛选抽屉外壳。
 *
 * 用法 (ProductOverview / MarketSurveyPage)：桌面上保留原本的筛选条
 * （用 ``hidden md:*`` 包起来）；手机端渲染本组件 —— 一个 44px 的
 * 筛选触发按钮（右上角带 active count 徽标），点击后从底部弹出覆盖
 * 88vh 的 Sheet，内容由父级作为 ``children`` 传入，底部固定 Reset /
 * Apply 两个大按钮方便单手操作。
 *
 * @param {number} activeCount    - 已激活的筛选条件数（显示角标用）
 * @param {() => void} onReset    - "重置" 回调
 * @param {string} applyLabel     - "应用" 按钮上的文案（通常是 "{total} 条记录"）
 * @param {string} testIdPrefix   - 测试 ID 前缀，默认 "m-filter-sheet"
 * @param {string} [title]        - 抽屉标题，默认 "筛选"
 * @param {React.ReactNode} children - 实际的筛选控件
 */
export const MobileFilterSheet = ({
  activeCount = 0,
  onReset,
  applyLabel,
  testIdPrefix = "m-filter-sheet",
  title,
  children,
}) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`${testIdPrefix}-trigger`}
        className={`md:hidden h-9 rounded-md shrink-0 gap-1 ${activeCount ? "border-brand text-brand" : ""}`}
      >
        <SlidersHorizontal size={15} />
        {activeCount > 0 && (
          <span
            className="px-1.5 rounded-full bg-brand text-white text-[10px] font-bold leading-4"
            data-testid={`${testIdPrefix}-count`}
          >
            {activeCount}
          </span>
        )}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="h-[88vh] overflow-y-auto"
          data-testid={`${testIdPrefix}-sheet`}
        >
          <SheetHeader>
            <SheetTitle>{title || t("products.filters")}</SheetTitle>
          </SheetHeader>
          <div className="mt-3 space-y-3 pb-28">{children}</div>
          <div className="fixed left-0 right-0 bottom-0 p-3 pb-[calc(env(safe-area-inset-bottom)+12px)] bg-card border-t border-border flex gap-2">
            <Button
              variant="outline"
              onClick={() => { onReset?.(); }}
              data-testid={`${testIdPrefix}-reset`}
              className="h-12 rounded-md flex-1"
            >
              <X size={15} /> {t("common.reset")}
            </Button>
            <Button
              onClick={() => setOpen(false)}
              data-testid={`${testIdPrefix}-apply`}
              className="h-12 rounded-md flex-1 bg-brand hover:bg-brand-hover"
            >
              {applyLabel}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
